import React, { useState, useEffect } from "react";
import "./LinkList.css";
import {
  getApiUrl,
  apiFetch,
  getSyncSettings,
  setSyncPreference,
  SyncAdapter,
} from "../utils/api";
import SyncDestinationPicker from "./SyncDestinationPicker";
import ConfirmDeleteButton from "./ConfirmDeleteButton";
import {
  loadDestinationTitles,
  resolveDestinationTitle,
  UNKNOWN_DESTINATION_LABEL,
} from "../utils/destination-titles";

interface LinkItem {
  id: string;
  url: string;
  domain: string;
  title?: string;
  status: string;
  created_at: string;
  processors?: string[];
  sync_target_page_id?: string | null;
  sync_target_url?: string | null;
  sync_target_adapter?: SyncAdapter | null;
}

interface LinkListProps {
  activeAdapter: SyncAdapter;
  onSelectLink: (id: string) => void;
  onRetry: (id: string) => void;
  onDelete: () => void;
  refreshTrigger?: number;
}

const LinkList: React.FC<LinkListProps> = ({
  activeAdapter,
  onSelectLink,
  onRetry,
  onDelete,
  refreshTrigger,
}) => {
  const [links, setLinks] = useState<LinkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncTargetId, setSyncTargetId] = useState<string | null>(null);
  const [pageTitles, setPageTitles] = useState<Record<string, string>>({});
  const [syncEnabled, setSyncEnabled] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, [refreshTrigger]);

  const fetchLinks = async () => {
    setError(null);
    const url = getApiUrl("/api/links");
    try {
      const response = await apiFetch("/api/links");
      if (response.ok) {
        const data = await response.json();
        setLinks(data);
        const settings = await getSyncSettings();
        setSyncEnabled(Boolean(settings.anyAdapterConnected));
        void preloadDestinationTitles();
      } else {
        setError(
          `Failed to fetch links: ${response.status} ${response.statusText} from ${url}`,
        );
      }
    } catch (error) {
      console.error("Failed to fetch links:", error);
      setError(
        `Network error: ${error instanceof Error ? error.message : String(error)} while connecting to ${url}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const preloadDestinationTitles = async () => {
    try {
      setPageTitles(await loadDestinationTitles(activeAdapter));
    } catch {
      console.warn("Failed to preload destination titles due to network/client error");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await apiFetch(`/api/links/${id}`, { method: "DELETE" });
      if (response.ok) {
        setLinks((prev) => prev.filter((l) => l.id !== id));
        onDelete();
      }
    } catch (error) {
      console.error("Failed to delete link:", error);
    }
  };

  const handleRetry = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      const response = await apiFetch(`/api/links/${id}/reprocess`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (response.ok) {
        onRetry(id);
      }
    } catch (error) {
      console.error("Failed to retry link:", error);
    }
  };

  if (loading) {
    return <div className="link-list-loading">Loading...</div>;
  }

  if (error) {
    return (
      <div className="link-list-error">
        <p>{error}</p>
        <button
          onClick={() => fetchLinks()}
          className="app-button secondary-button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (links.length === 0) {
    return (
      <div className="link-list-empty">
        No links yet. Share a link to get started!
      </div>
    );
  }

  return (
    <div className="link-list">
      {syncEnabled && syncTargetId && (
        <SyncDestinationPicker
          linkId={syncTargetId}
          activeAdapter={activeAdapter}
          presetPageId={
            links.find((link) => link.id === syncTargetId)?.sync_target_page_id
          }
          presetPageTitle={(() => {
            const pageId =
              links.find((link) => link.id === syncTargetId)?.sync_target_page_id ||
              undefined;
            return resolveDestinationTitle(pageId, pageTitles);
          })()}
          onSelect={async (pageId, adapter) => {
            try {
              await setSyncPreference(syncTargetId, pageId, adapter || activeAdapter);
            } catch (err) {
              console.error("Failed to set sync preference:", err);
            }
            setSyncTargetId(null);
          }}
          onClose={() => setSyncTargetId(null)}
        />
      )}
      {links.map((link) => (
        <div
          key={link.id}
          className={`link-item link-item--${link.status}`}
          onClick={() => onSelectLink(link.id)}
        >
          <div className="link-item-content">
            <div className="link-item-title">{link.title || link.url}</div>
            <div className="link-item-meta">
              <span className="link-item-domain">{link.domain}</span>
              {link.sync_target_page_id && (
                link.sync_target_url ? (
                  <a className="link-item-destination" href={link.sync_target_url} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>
                    ↳ {resolveDestinationTitle(link.sync_target_page_id, pageTitles) ?? UNKNOWN_DESTINATION_LABEL}
                  </a>
                ) : <span className="link-item-destination">↳ {resolveDestinationTitle(link.sync_target_page_id, pageTitles) ?? UNKNOWN_DESTINATION_LABEL}</span>
              )}
              {/* One chip per processor, not per result: reprocessing a link
                  leaves several results from the same processor behind. */}
              {[...new Set(link.processors ?? [])].map((p) => (
                <span key={p} className="link-item-processor">
                  {p}
                </span>
              ))}
              <span className={`link-item-status status--${link.status}`}>
                {link.status}
              </span>
            </div>
          </div>
          <div className="link-item-actions">
            {link.status === "done" && syncEnabled && (
              <button
                className="link-action-btn sync-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setSyncTargetId(link.id);
                }}
                title="Choose destination"
              >
                <i className="fas fa-cloud-upload-alt"></i>
              </button>
            )}
            {link.status === "failed" && (
              <button
                className="link-action-btn retry-btn"
                onClick={(e) => handleRetry(e, link.id)}
                title="Retry"
              >
                <i className="fas fa-redo"></i>
              </button>
            )}
            <ConfirmDeleteButton
              className="link-action-btn delete-btn"
              confirmClassName="delete-btn--confirm"
              onConfirm={async () => {
                await handleDelete(link.id);
              }}
              stopPropagation
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default LinkList;
